/**
 * ERP Item Import Renderer (ERP-ONBOARDING slice 5, design O13).
 *
 * Renders the thing the progress bar structurally cannot: WHY the numbers are what
 * they are. `successCount` alone is a trap on this operation — the import's equality
 * damper drops every row already at the right value, so the second run of a perfectly
 * healthy import imports zero rows. Reading "0 of 5,000" without the breakdown, an
 * operator concludes the integration broke.
 *
 * So the four buckets are shown side by side, and they PARTITION the total:
 *
 *     imported + unchanged + unresolved + errored  =  totalItems
 *
 * `unchanged` is the benign, expected majority on any re-run; `unresolved` is the one
 * that deserves attention (rows the ERP returned without a usable product id, which
 * were deliberately NOT merged onto a shared item).
 *
 * ── Read the counts through the SHARED selectors, never the raw row fields ────
 * Only `markCompleted` ever writes `successCount` / `errorCount` onto the row. An
 * ERRORED run never reaches it: the running policy calls
 * `createBatchProgressAdvancer.recordError`, which writes only
 * `{ completedCount, erroredTargetIds, status, updatedAt }`. So on the run an operator
 * is most likely to open, every raw counter field is ABSENT — and reading them
 * directly renders 0/0/0/0 exactly when something went wrong, directly below a
 * progress bar that (going through `progressSelectors`, which falls back to
 * `erroredTargetIds.length`) reports the real error count. Two contradictory numbers
 * on one screen for one row. Going through the same `successCount`/`errorCount`
 * selectors as the bar is what structurally prevents that drift.
 *
 * `projectedCount` is `null` here: the detail renderer is not handed the
 * batch-progress projection, so success falls back to the write-side
 * `completedCount - errorCount` — which is precisely the number `recordError`
 * persisted.
 *
 * Caveat, deliberately not papered over: on an errored run the two SKIP buckets are
 * genuinely not yet reported (only the completion event carries them), so they read 0
 * and the partition identity holds only once the run completes.
 *
 * Data shape (from ErpItemImportBatchProcessStartedEventSchema):
 *   connectorId: string
 *   integrationUserId: string
 */

import { FC } from 'react';
import { Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconPlug } from '@tabler/icons-react';
import type { ProgressProcess } from '../../../shared/state/progressSelectors';
import { renderErpImportBreakdown } from './ErpImportBreakdown';
import type { DetailRendererProps, OperationRendererConfig, ProcessData } from './index';

const DetailComponent: FC<DetailRendererProps> = ({ process, t }) => {
  const data = ( process.data || {}) as ProcessData;
  // `ProgressProcess` is the readmodel row PLUS the completion-carried counters
  // (`successCount` / `errorCount`, which the service writes but the row schema does
  // not declare). Every added member is optional, so a plain readmodel row widens to
  // it without a cast — and this renderer reads it through the same selectors the
  // progress bar uses, so neither can quietly invent its own fallback rule.
  //
  // ERRORED is then genuinely identical to the bar's number: `errorCount` ignores
  // `projectedCount`, so both call sites compute it from the same inputs. IMPORTED is
  // not: the bar feeds `state.projectedSuccessCount` (`detail/state/state.ts`) while
  // this renderer has no access to the projection and passes `null` (see the note
  // above), so once the batch-progress projection has loaded the two can legitimately
  // differ while a run is still converging.
  const counters: ProgressProcess = process;

  return (
    <Stack gap="sm">
      <Text fz="sm" c="dimmed">
        {t(
          'batchProcesses_operations_ErpItemImport_description',
          'Importación del catálogo completo del ERP.'
        )}
      </Text>

      <Group gap="xs">
        <Text fz="sm" fw={500}>
          {t( 'batchProcesses_renderer_sourceErp', 'ERP de origen' )}:
        </Text>
        <ThemeIcon size="sm" variant="light" color="gray">
          <IconPlug size={14} />
        </ThemeIcon>
        <Text fz="sm">
          {data.connectorId ?? t( 'batchProcesses_renderer_none' )}
        </Text>
      </Group>

      {/*
        * The breakdown — SHARED with the stock and price renderers
        * (`ErpImportBreakdown.tsx`); the three copies this file used to carry were
        * byte-identical. Rendered whenever the run has settled, including when every
        * counter but `unchanged` is zero, which is exactly the re-run case the operator
        * must be able to read as "nothing to do", not as "nothing worked".
        */}
      {renderErpImportBreakdown( counters, t )}
    </Stack>
  );
};

export const config: Omit<OperationRendererConfig, 'icon' | 'color' | 'labelKey'> = {
  getSummary: ( data, t ) => {
    const connectorId = data?.connectorId;
    if ( connectorId ) {
      return t( 'batchProcesses_summary_fromErp', { erp: connectorId, defaultValue: `Desde ${ connectorId }` });
    }
    return t( 'batchProcesses_summary_unknownOperation' );
  },
  DetailComponent,
  // The targets are Teselly items, not publications/warehouses/channels — nothing in
  // the shared reference-data resolver applies.
  getRequiredReferenceIds: () => ({})
};

export default DetailComponent;
